/**
 * Builds the pre-populated destination event when a record is moved from one
 * record type to another.
 *
 * The source record is left untouched here (the caller closes it as COMPLETED
 * with its `moveStatus` set). This module produces a fresh, unsaved event for
 * the Create Event window: it starts from a clean template, carries over the
 * fields that apply to the destination record type, copies all associated notes
 * (preserving their original timestamps and logged users), defects, barriers,
 * and ATA classifications, and stamps `movedFrom` so the destination shows the
 * blue lineage dot on the homepage.
 */
import { UnscheduledEvent } from '../../types';
import { EVENT_STATUS } from '../../constants';
import { createEmptyEvent } from './createEmptyEvent';

/** Fields shared by every record type, carried over on any move. */
interface SharedCarryover {
  aircraftType?: string;
  tailNumber: string;
  carrier: string;
  station: string;
  ataCodeId: number;
  ataChapter?: string;
  ataSection?: string;
  correctiveAction: string;
  timeDeclaredZulu: string;
  highPriorityEvent: boolean;
  deaImpactedPackageCount: number;
  revisionCounter: number;
  summary: string;
}

/**
 * The destination's starting status. A move preserves how far along the work is,
 * so an active source (IN_WORK/PENDING) carries that status through; anything
 * else defaults to IN_WORK for the new active record.
 */
function carriedStatus(sourceStatus: string): string {
  return sourceStatus === EVENT_STATUS.PENDING ? EVENT_STATUS.PENDING : EVENT_STATUS.IN_WORK;
}

/** Extracts the date portion (YYYY-MM-DD) from a Zulu date-time string. */
function datePart(zulu: string): string {
  return zulu ? zulu.split(/[ T]/)[0] || '' : '';
}

/** Picks the fields shared across all record types from the source record. */
function sharedCarryover(source: UnscheduledEvent): SharedCarryover {
  return {
    aircraftType: source.aircraftType,
    tailNumber: source.tailNumber,
    carrier: source.carrier,
    station: source.station,
    ataCodeId: source.ataCodeId,
    ataChapter: source.ataChapter,
    ataSection: source.ataSection,
    correctiveAction: source.correctiveAction,
    timeDeclaredZulu: source.timeDeclaredZulu,
    highPriorityEvent: source.highPriorityEvent,
    deaImpactedPackageCount: source.deaImpactedPackageCount,
    revisionCounter: source.revisionCounter,
    summary: source.summary,
  };
}

/** Copies the child collections (notes keep their original timestamps/users). */
function childCarryover(source: UnscheduledEvent): Partial<UnscheduledEvent> {
  return {
    defects: source.defects ? source.defects.map((defect) => ({ ...defect })) : [],
    // Notes keep their original timestamps/users and are flagged as carried
    // forward so the destination waives the not-before-Time-Declared rule for
    // them (their original times may predate the new record's Time Declared).
    noteEntries: source.noteEntries
      ? source.noteEntries.map((note) => ({ ...note, carriedForward: true }))
      : [],
    techNotes: source.techNotes?.map((note) => ({ ...note })) ?? [],
    barriers: source.barriers ? source.barriers.map((barrier) => ({ ...barrier })) : [],
    selectedChapters: { ...(source.selectedChapters || {}) },
    selectedSections: { ...(source.selectedSections || {}) },
    defectHistory: { ...(source.defectHistory || {}) },
  };
}

/** Adds the IROP, flight, ERTS, and delay fields for a Planned/Unscheduled target. */
function fullDetailCarryover(source: UnscheduledEvent): Partial<UnscheduledEvent> {
  return {
    inboundFlightNumber: source.inboundFlightNumber,
    outboundFlightNumber: source.outboundFlightNumber,
    nextScheduledFlightNumber: source.nextScheduledFlightNumber,
    nextScheduledFlightTimeZulu: source.nextScheduledFlightTimeZulu,
    previousFlightScheduledDepartureTimeZulu: source.previousFlightScheduledDepartureTimeZulu,
    chargeableFlightScheduledDepartureTimeZulu: source.chargeableFlightScheduledDepartureTimeZulu,
    delayTimeMinutes: source.delayTimeMinutes,
    returnToBlock: source.returnToBlock,
    abortedTakeOff: source.abortedTakeOff,
    airTurnBack: source.airTurnBack,
    diversion: source.diversion,
    cancellation: source.cancellation,
    delay: source.delay,
    damage: source.damage,
    damageCode: source.damageCode,
    chargeable: source.chargeable,
    nextAdviseTimeZulu: source.nextAdviseTimeZulu,
    returnToServiceTimeZulu: source.returnToServiceTimeZulu,
    initialReturnToServiceTimeZulu: source.initialReturnToServiceTimeZulu,
    returnToServiceChangeReason: source.returnToServiceChangeReason,
    returnToServiceConfidence: source.returnToServiceConfidence,
  };
}

/** Adds the ERTS/Next-Advise fields a Scheduled target carries (no IROP/flights). */
function scheduledCarryover(source: UnscheduledEvent): Partial<UnscheduledEvent> {
  return {
    nextAdviseTimeZulu: source.nextAdviseTimeZulu,
    returnToServiceTimeZulu: source.returnToServiceTimeZulu,
    returnToServiceConfidence: source.returnToServiceConfidence,
  };
}

/** Adds the Deferred-only fields, defaulting the Due Date from the source ERTS. */
function deferredCarryover(source: UnscheduledEvent): Partial<UnscheduledEvent> {
  return {
    dueDateZulu: source.dueDateZulu || datePart(source.returnToServiceTimeZulu),
    apuDeferral: source.apuDeferral || false,
    etopsRestricted: source.etopsRestricted || false,
    catIiiRestricted: source.catIiiRestricted || false,
    operationallyRestrictive: source.operationallyRestrictive || false,
    catType: source.catType || '',
  };
}

/** Returns the per-type field additions for the destination record type. */
function typeSpecificCarryover(
  source: UnscheduledEvent,
  targetRecordType: string,
): Partial<UnscheduledEvent> {
  if (targetRecordType === 'Deferred') return deferredCarryover(source);
  if (targetRecordType === 'Scheduled') return scheduledCarryover(source);
  return fullDetailCarryover(source);
}

/**
 * Builds a fresh, unsaved destination event pre-populated from `source` for the
 * given target record type. The returned event has no id/version (created on
 * save), carries the source's active status, and records `movedFrom` so the
 * destination surfaces the blue lineage dot.
 */
export function buildMovedEvent(
  source: UnscheduledEvent,
  targetRecordType: string,
): UnscheduledEvent {
  const moved: UnscheduledEvent = {
    ...createEmptyEvent(),
    ...sharedCarryover(source),
    ...typeSpecificCarryover(source, targetRecordType),
    ...childCarryover(source),
    recordType: targetRecordType,
    movedFrom: source.recordType || 'Unscheduled',
    status: carriedStatus(source.status),
  };
  return moved;
}
